import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'page' | 'component';
  context?: Record<string, any>;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  eventId?: string;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Log error details for debugging
    console.error('[Error Boundary] Component error:', {
      timestamp: new Date().toISOString(),
      error: {
        message: error.message,
        name: error.name,
        stack: error.stack,
      },
      componentStack: errorInfo.componentStack,
      errorBoundary: {
        level: this.props.level || 'component',
        context: this.props.context,
      },
      errorLevel: this.props.level || 'component',
    });

    // Generate a simple error ID for debugging
    const errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    this.setState({
      error,
      errorInfo,
      eventId: errorId,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReportFeedback = () => {
    if (this.state.eventId) {
      console.log('[Error Boundary] User requested to report feedback:', {
        timestamp: new Date().toISOString(),
        errorId: this.state.eventId,
      });
      // In a production app, this could redirect to a feedback form
      alert('Thank you for wanting to report this issue! Please contact support@gracefuljourney.com with the Error ID: ' + this.state.eventId);
    }
  };

  render() {
    if (this.state.hasError) {
      // If a fallback component is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      const isPageLevel = this.props.level === 'page';

      return (
        <div className={`flex items-center justify-center ${isPageLevel ? 'min-h-screen bg-gray-50' : 'p-8'}`}>
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            {/* Error Icon */}
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>

            {/* Error Title */}
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
              {isPageLevel ? 'Something went wrong' : 'Component Error'}
            </h2>

            {/* Error Description */}
            <p className="text-gray-600 text-center mb-6">
              {isPageLevel 
                ? "We're sorry, but something unexpected happened. Our team has been notified and is working to fix this issue."
                : "This component encountered an error. You can try refreshing the page or continue using other parts of the site."
              }
            </p>

            {/* Development Error Details */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                <h3 className="text-sm font-medium text-red-800 mb-2">Error Details (Development Only)</h3>
                <div className="text-xs text-red-700 font-mono overflow-auto max-h-32">
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.message}
                  </div>
                  <div className="mb-2">
                    <strong>Stack:</strong>
                    <pre className="whitespace-pre-wrap">{this.state.error.stack}</pre>
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre className="whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3">
              <button
                onClick={this.handleReload}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                {isPageLevel ? 'Reload Page' : 'Refresh Component'}
              </button>

              {/* Report Feedback Button (only if eventId exists) */}
              {this.state.eventId && (
                <button
                  onClick={this.handleReportFeedback}
                  className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Report Issue
                </button>
              )}

              {/* Go Home Button (for page-level errors) */}
              {isPageLevel && (
                <button
                  onClick={() => window.location.href = '/'}
                  className="w-full text-blue-600 py-2 px-4 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Go to Homepage
                </button>
              )}
            </div>

            {/* Contact Information */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                If this problem persists, please contact us at{' '}
                <a
                  href="mailto:support@gracefuljourney.com"
                  className="text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    // Track contact attempt from error boundary
                    console.log('[Error Boundary] User contacted support:', {
                      timestamp: new Date().toISOString(),
                      message: 'User contacted support from error boundary',
                      errorId: this.state.eventId,
                      errorLevel: this.props.level,
                    });
                  }}
                >
                  support@gracefuljourney.com
                </a>
              </p>
            </div>

            {/* Error ID (for debugging) */}
            {this.state.eventId && (
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-400">
                  Error ID: {this.state.eventId}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC for easy wrapping of components
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Specialized error boundaries for common use cases
export const PageErrorBoundary: React.FC<{ children: ReactNode; context?: Record<string, any> }> = ({ 
  children, 
  context 
}) => (
  <ErrorBoundary level="page" context={context}>
    {children}
  </ErrorBoundary>
);

export const ComponentErrorBoundary: React.FC<{ 
  children: ReactNode; 
  context?: Record<string, any>;
  fallback?: ReactNode;
}> = ({ children, context, fallback }) => (
  <ErrorBoundary level="component" context={context} fallback={fallback}>
    {children}
  </ErrorBoundary>
);

// Hook for programmatic error reporting (console logging)
export const useErrorReporting = () => {
  const reportError = (error: Error, context?: Record<string, any>) => {
    const errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.error('[Error Reporting] User reported error:', {
      timestamp: new Date().toISOString(),
      errorId,
      error: {
        message: error.message,
        name: error.name,
        stack: error.stack,
      },
      context,
      userReported: true,
    });
    return errorId;
  };

  const reportMessage = (message: string, level: 'info' | 'warning' | 'error' = 'info', context?: Record<string, any>) => {
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const logLevel = level === 'error' ? 'error' : level === 'warning' ? 'warn' : 'log';
    console[logLevel]('[Error Reporting] User reported message:', {
      timestamp: new Date().toISOString(),
      messageId,
      message,
      level,
      context,
      userReported: true,
    });
    return messageId;
  };

  return { reportError, reportMessage };
};

export default ErrorBoundary;
